import React, { useEffect } from "react";
import {
    Flex,
    Grid,
    SimpleGrid,
    Text,
    Box,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import MiniStatistics from "../Dashboard/components/MiniStatistics";

import ProgrammeOverviewChart from "./components/ProgrammeOverviewChart";
import CertificationOverviewChart from "./components/CertificationOverviewChart";
import AgentPerformanceChart from "./components/AgentPerformanceChart";
import TopPerformingAgentsChart from "./components/TopPerformingAgentsChart";
import AnalyticsTable from "./components/AnalyticsTable";

import {
    Users,
    UserCheck,
    Award,
    DollarSign,
    Wallet,
} from "lucide-react";

import {
    fetchAgentAnalyticsDashboardAction,
    fetchAgentProgrammeAction,
    fetchAgentCertificationAction,
    fetchAgentOperationsAction,
    fetchTopPerformingAgentsAction,
    fetchAgentPerformanceAction,
    fetchAgentCustomerSatisfactionAction,
} from "../../../state/actions/agentAnalyticsActions";

export default function AgentAnalytics() {
    const dispatch = useDispatch();

    const {
        dashboard,
        programme,
        certification,
        operations,
        topPerformingAgents,
        agentPerformance,
        customerSatisfaction,

        dashboardLoading,
        programmeLoading,
        certificationLoading,
        operationsLoading,
        topPerformingAgentsLoading,
        agentPerformanceLoading,
        customerSatisfactionLoading,

        dashboardError,
        programmeError,
        certificationError,
        operationsError,
        topPerformingAgentsError,
        agentPerformanceError,
        customerSatisfactionError,
    } = useSelector(
        (state) => state.agentAnalytics
    );

    useEffect(() => {
        dispatch(fetchAgentAnalyticsDashboardAction());
        dispatch(fetchAgentProgrammeAction());
        dispatch(fetchAgentCertificationAction());
        dispatch(fetchAgentOperationsAction());
        dispatch(fetchTopPerformingAgentsAction());
        dispatch(fetchAgentPerformanceAction());
        dispatch(fetchAgentCustomerSatisfactionAction());
    }, [dispatch]);

    const loading =
        dashboardLoading ||
        programmeLoading ||
        certificationLoading ||
        operationsLoading ||
        topPerformingAgentsLoading ||
        agentPerformanceLoading ||
        customerSatisfactionLoading;

    const error =
        dashboardError ||
        programmeError ||
        certificationError ||
        operationsError ||
        topPerformingAgentsError ||
        agentPerformanceError ||
        customerSatisfactionError;

    const programmeData =
        dashboard?.programme || programme || {};

    const certificationData =
        dashboard?.certification || certification || {};

    const operationsData =
        dashboard?.operations || operations || {};

    return (
        <Flex
            flexDirection="column"
            pt={{ base: "120px", md: "75px" }}
        >
           

            {error && (
                <Text
                    color="red.500"
                    mb={4}
                >
                    {error}
                </Text>
            )}

            <SimpleGrid
                columns={{
                    sm: 1,
                    md: 2,
                    xl: 5,
                }}
                spacing="24px"
            >
                <MiniStatistics
                    title="Total Agents"
                    amount={
                        programmeData.total_agents ?? 0
                    }
                    percentage={0}
                    icon={
                        <Users size={22} />
                    }
                    isLoading={loading}
                />

                <MiniStatistics
                    title="Active Agents"
                    amount={
                        programmeData.active_agents ?? 0
                    }
                    percentage={0}
                    icon={
                        <UserCheck size={22} />
                    }
                    isLoading={loading}
                />

                <MiniStatistics
                    title="Certified Agents"
                    amount={
                        certificationData.certified_agents ?? 0
                    }
                    percentage={0}
                    icon={
                        <Award size={22} />
                    }
                    isLoading={loading}
                />

                <MiniStatistics
                    title="Revenue Generated"
                    amount={`P${Number(
                        operationsData.revenue_generated ?? 0
                    ).toLocaleString()}`}
                    percentage={0}
                    icon={
                        <DollarSign size={22} />
                    }
                    isLoading={loading}
                />

                <MiniStatistics
                    title="Commission Liability"
                    amount={`P${Number(
                        operationsData.commission_liability ?? 0
                    ).toLocaleString()}`}
                    percentage={0}
                    icon={
                        <Wallet size={22} />
                    }
                    isLoading={loading}
                />
            </SimpleGrid>

            <Grid
                templateColumns={{
                    sm: "1fr",
                    md: "1fr 1fr",
                }}
                gap="24px"
                mt={6}
            >
                <ProgrammeOverviewChart
                    data={programmeData}
                    loading={loading}
                />

                <CertificationOverviewChart
                    data={certificationData}
                    loading={loading}
                />
            </Grid>

            <Grid
                templateColumns={{
                    sm: "1fr",
                    md: "1fr 1fr",
                }}
                gap="24px"
                mt={6}
            >
                <AgentPerformanceChart
                    data={agentPerformance}
                    loading={loading}
                />

                <TopPerformingAgentsChart
                    data={topPerformingAgents}
                    loading={loading}
                />
            </Grid>

            <Grid mt={6}>
                <AnalyticsTable
                    title="Top Performing Agents"
                    amount={topPerformingAgents.length}
                    captions={[
                        "Rank",
                        "Agent",
                        "Agent Number",
                        "Packages",
                        "Revenue",
                        "Commission",
                        "Score",
                    ]}
                    data={topPerformingAgents}
                    isLoading={loading}
                    variant="topPerformingAgents"
                />
            </Grid>

            <Grid mt={6}>
                <AnalyticsTable
                    title="Agent Performance"
                    amount={agentPerformance.length}
                    captions={[
                        "Agent",
                        "Agent Number",
                        "Status",
                        "Packages",
                        "Revenue",
                        "Commission",
                        "Score",
                    ]}
                    data={agentPerformance}
                    isLoading={loading}
                    variant="agentPerformance"
                />
            </Grid>

            <Grid
                templateColumns={{
                    sm: "1fr",
                    md: "1fr 1fr",
                }}
                gap="24px"
                mt={6}
            >
                <AnalyticsTable
                    title="Agent Programme"
                    amount={
                        programmeData.total_agents ?? 0
                    }
                    captions={[
                        "Metric",
                        "Count",
                    ]}
                    data={[
                        {
                            metric: "Pending Applications",
                            count:
                                programmeData.pending_applications ??
                                0,
                        },
                        {
                            metric: "Approved Agents",
                            count:
                                programmeData.approved_agents ??
                                0,
                        },
                        {
                            metric: "Active Agents",
                            count:
                                programmeData.active_agents ??
                                0,
                        },
                        {
                            metric: "Suspended Agents",
                            count:
                                programmeData.suspended_agents ??
                                0,
                        },
                        {
                            metric: "Rejected Agents",
                            count:
                                programmeData.rejected_agents ??
                                0,
                        },
                        {
                            metric: "Deactivated Agents",
                            count:
                                programmeData.deactivated_agents ??
                                0,
                        },
                    ]}
                    isLoading={loading}
                    variant="programme"
                />

                <AnalyticsTable
                    title="Certification Overview"
                    amount={
                        certificationData.certified_agents ??
                        0
                    }
                    captions={[
                        "Metric",
                        "Count",
                    ]}
                    data={[
                        {
                            metric: "Agents In Training",
                            count:
                                certificationData.agents_in_training ??
                                0,
                        },
                        {
                            metric: "Awaiting Certification",
                            count:
                                certificationData.agents_awaiting_certification ??
                                0,
                        },
                        {
                            metric: "Certified Agents",
                            count:
                                certificationData.certified_agents ??
                                0,
                        },
                        {
                            metric: "Expired Certifications",
                            count:
                                certificationData.expired_certifications ??
                                0,
                        },
                        {
                            metric: "Not Started",
                            count:
                                certificationData.not_started ??
                                0,
                        },
                        {
                            metric: "Failed",
                            count:
                                certificationData.failed ??
                                0,
                        },
                    ]}
                    isLoading={loading}
                    variant="certification"
                />
            </Grid>

            <Grid mt={6} mb={6}>
                <AnalyticsTable
                    title="Agent Operations"
                    amount={
                        operationsData.packages_created_by_agents ??
                        0
                    }
                    captions={[
                        "Metric",
                        "Value",
                    ]}
                    data={[
                        {
                            metric: "Packages Created By Agents",
                            value:
                                operationsData.packages_created_by_agents ??
                                0,
                        },
                        {
                            metric: "Revenue Generated",
                            value: `P${Number(
                                operationsData.revenue_generated ??
                                    0
                            ).toLocaleString()}`,
                        },
                        {
                            metric: "Commission Liability",
                            value: `P${Number(
                                operationsData.commission_liability ??
                                    0
                            ).toLocaleString()}`,
                        },
                        {
                            metric: "Average Packages Per Agent",
                            value:
                                operationsData.average_packages_per_agent ??
                                0,
                        },
                    ]}
                    isLoading={loading}
                    variant="operations"
                />
            </Grid>
        </Flex>
    );
}